import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useTheme,
  Slide
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Info,
  Close
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Transition component for smooth dialog animation
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MotionBox = motion.div;

/**
 * Reusable Alert Dialog Component
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Close handler
 * @param {string} props.type - Alert type: 'success', 'error', 'warning', 'info'
 * @param {string} props.title - Dialog title (optional, uses default based on type)
 * @param {string} props.message - Alert message
 * @param {string} props.confirmText - Confirm button text (optional)
 * @param {function} props.onConfirm - Confirm handler (optional, defaults to onClose)
 * @param {boolean} props.autoClose - Auto close after delay (optional)
 * @param {number} props.autoCloseDelay - Auto close delay in ms (default: 3000)
 */
const AlertDialog = ({
  open,
  onClose,
  type = 'success',
  title,
  message,
  confirmText,
  onConfirm,
  autoClose = true,
  autoCloseDelay = 3000
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Auto close effect
  React.useEffect(() => {
    if (open && autoClose && type === 'success') {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [open, autoClose, autoCloseDelay, onClose, type]);

  // Get configuration based on alert type
  const getAlertConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle sx={{ fontSize: 60 }} />,
          iconColor: theme.palette.success.main,
          bgColor: theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)',
          borderColor: theme.palette.success.main,
          defaultTitle: t('common.success') || 'Success',
          defaultConfirmText: t('common.continue') || 'Continue'
        };
      case 'error':
        return {
          icon: <Error sx={{ fontSize: 60 }} />,
          iconColor: theme.palette.error.main,
          bgColor: theme.palette.error.lighter || 'rgba(244, 67, 54, 0.1)',
          borderColor: theme.palette.error.main,
          defaultTitle: t('common.error') || 'Error',
          defaultConfirmText: t('common.tryAgain') || 'Try Again'
        };
      case 'warning':
        return {
          icon: <Warning sx={{ fontSize: 60 }} />,
          iconColor: theme.palette.warning.main,
          bgColor: theme.palette.warning.lighter || 'rgba(255, 193, 7, 0.1)',
          borderColor: theme.palette.warning.main,
          defaultTitle: t('common.warning') || 'Warning',
          defaultConfirmText: t('common.understood') || 'Understood'
        };
      case 'info':
        return {
          icon: <Info sx={{ fontSize: 60 }} />,
          iconColor: theme.palette.info.main,
          bgColor: theme.palette.info.lighter || 'rgba(33, 150, 243, 0.1)',
          borderColor: theme.palette.info.main,
          defaultTitle: t('common.information') || 'Information',
          defaultConfirmText: t('common.okay') || 'Okay'
        };
      default:
        return getAlertConfig('success');
    }
  };

  const config = getAlertConfig();
  const dialogTitle = title || config.defaultTitle;
  const buttonText = confirmText || config.defaultConfirmText;
  const handleConfirm = onConfirm || onClose;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `2px solid ${config.borderColor}`,
          overflow: 'visible'
        }
      }}
    >
      <DialogContent sx={{ 
        textAlign: 'center', 
        py: 4, 
        px: 3,
        backgroundColor: config.bgColor,
        position: 'relative'
      }}>
        <MotionBox
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.1 
          }}
        >
          <Box sx={{ 
            color: config.iconColor,
            mb: 2,
            display: 'flex',
            justifyContent: 'center'
          }}>
            {config.icon}
          </Box>
        </MotionBox>

        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 2, 
              color: config.iconColor 
            }}
          >
            {dialogTitle}
          </Typography>

          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3,
              lineHeight: 1.6,
              color: 'text.primary'
            }}
          >
            {message}
          </Typography>
        </MotionBox>

        {/* Auto-close indicator for success */}
        {type === 'success' && autoClose && (
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('common.autoClosing') || 'Auto-closing in a few seconds...'}
            </Typography>
          </MotionBox>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        justifyContent: 'center', 
        pb: 3, 
        px: 3,
        backgroundColor: config.bgColor
      }}>
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="large"
            sx={{
              minWidth: 140,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: config.iconColor,
              '&:hover': {
                backgroundColor: config.iconColor,
                filter: 'brightness(0.9)'
              }
            }}
          >
            {buttonText}
          </Button>
        </MotionBox>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDialog;